import get from 'lodash/get';

import {getSubset} from '~/src/utilities/feed/sitemap';
import {SiteArea} from '~/src/utilities/constants/navigation';
import {createFormat} from '~/src/utilities/feed/format';
import {ColorScheme, TeaserFormat} from '~/src/utilities/constants/formats';
import {getJsonRequest} from '~/src/utilities/helpers/request';
import {TeaserTypes, ContentType, ElementTypes} from '~/src/utilities/constants/types';
import {Logger} from '~/src/utilities/helpers/log';

/**
 * Lifestyle related helpers
 */

/**
 * Get the first lifestyle subcategory (AKA the lifestyle front)
 */
const getLifestyleFront = (sitemap) => getSubset(sitemap, SiteArea.LIFESTYLE)[0];

/**
 * Calculate the format for a teaser in the lifestyle front
 *
 * @param {object} element feed teaser element
 * @param {number} index index of the teaser element
 * @param {number} sectionIndex index of the teaser's section
 * @param {number} totalContentElements total number of teasers in the current section
 */
const getLifestyleFrontFormat = (element, index, sectionIndex, totalContentElements) => {
    const colorScheme = sectionIndex > 0 ? ColorScheme.SECONDARY : ColorScheme.PRIMARY;

    if (sectionIndex === 0 && totalContentElements % 5 === 0) {
        switch (index % 5) {
            case 0:
                return createFormat(TeaserFormat.TEASER_LARGE, TeaserFormat.TEASER_FULL, colorScheme);
            case 3:
            case 4:
                return createFormat(TeaserFormat.TEASER_SMALL, TeaserFormat.TEASER_FULL, colorScheme);
            default:
                return createFormat(TeaserFormat.TEASER_SMALL, null, colorScheme);
        }
    } else {
        if (totalContentElements === 2) {
            return createFormat(index === 0 ? TeaserFormat.TEASER_SMALL : TeaserFormat.TEASER_LARGE, TeaserFormat.TEASER_SMALL, colorScheme);
        }
        if (index === 0) {
            return createFormat(TeaserFormat.TEASER_FULL, null, colorScheme);
        }
        if (index === totalContentElements - 1) {
            if (totalContentElements === 3) {
                return createFormat(TeaserFormat.TEASER_LARGE, TeaserFormat.TEASER_SMALL, colorScheme);
            }
            switch ((totalContentElements - 1) % 3) {
                case 0:
                    return createFormat(TeaserFormat.TEASER_SMALL, TeaserFormat.TEASER_FULL, colorScheme);
                case 1:
                    return createFormat(TeaserFormat.TEASER_FULL, TeaserFormat.TEASER_SMALL, colorScheme);
                case 2:
                    return createFormat(TeaserFormat.TEASER_LARGE, TeaserFormat.TEASER_FULL, colorScheme);
                default:
                    return createFormat(TeaserFormat.TEASER_LARGE, null, colorScheme);
            }
        }
    }
    return createFormat(TeaserFormat.TEASER_SMALL, null, colorScheme);
};

/**
 * Calculate the format for a teaser in the lifestyle channels
 *
 * @param {object} element feed teaser element
 * @param {number} index index of the teaser element
 * @param {number} sectionIndex index of the teaser's section
 * @param {number} totalContentElements total number of teasers in the current section
 */
const getLifestyleChannelFormat = (element, index, sectionIndex, totalContentElements) => {
    const colorScheme = sectionIndex > 0 || index > 4 ? ColorScheme.SECONDARY : ColorScheme.PRIMARY;

    let primaryFormat = TeaserFormat.TEASER_SMALL;
    let secondaryFormat = TeaserFormat.TEASER_SMALL;

    switch (totalContentElements) {
        case 1:
            primaryFormat = TeaserFormat.TEASER_FULL;
            secondaryFormat = TeaserFormat.TEASER_FULL;
            break;
        case 2:
            if (index === 0) {
                primaryFormat = TeaserFormat.TEASER_LARGE;
            }
            secondaryFormat = TeaserFormat.TEASER_LARGE;
            break;
        case 3:
            if (index === 0) {
                secondaryFormat = TeaserFormat.TEASER_FULL;
            }
            break;
        case 4:
            if (index === 0 || index === 4) {
                primaryFormat = TeaserFormat.TEASER_LARGE;
                secondaryFormat = TeaserFormat.TEASER_FULL;
            }
            break;
        default:
            if (sectionIndex === 0) {
                if (index === 0) {
                    primaryFormat = TeaserFormat.TEASER_LARGE;
                    secondaryFormat = TeaserFormat.TEASER_FULL;
                }
                if (index === 5) {
                    primaryFormat = TeaserFormat.TEASER_FULL;
                    secondaryFormat = TeaserFormat.TEASER_FULL;
                }
            }
            break;
    }

    if (totalContentElements > 6 && index === totalContentElements - 1) {
        const totalSmallTeasers = totalContentElements - 6;
        if (totalSmallTeasers % 3 === 1) {
            primaryFormat = TeaserFormat.TEASER_FULL;
        }
        if (totalSmallTeasers % 3 === 2) {
            primaryFormat = TeaserFormat.TEASER_LARGE;
        }
        if (totalSmallTeasers % 2 === 1) {
            secondaryFormat = TeaserFormat.TEASER_FULL;
        }
    }

    return createFormat(primaryFormat, secondaryFormat, colorScheme);
};

/**
 * fetches and stores a quote and some article data for
 * the custom section type 'lifestylequote'
 *
 * @param {object} section custom section config
 */
const getLifestyleQuote = async (section) => {
    try {
        const data = await getJsonRequest(section.url);
        if (data.type === ContentType.ARTICLE) {
            const elements = get(data, 'content.article.elements', []);
            const quotes = elements.filter((element) => element.type === ElementTypes.QUOTE);
            const pos = Math.min(section.options.pos, quotes.length);
            if (pos !== 0) {
                const community = get(data, 'content.meta.clientdata.community', {});
                const settings = get(data, 'content.meta.settings', {});
                const colorTheme = section.options.theme === '2' ? ColorScheme.SECONDARY : ColorScheme.PRIMARY;
                section.elements.push({
                    type: TeaserTypes.LIFESTYLE_QUOTE,
                    id: data.id,
                    community,
                    settings,
                    url: get(data, 'content.meta.url'),
                    format: createFormat(TeaserFormat.TEASER_FULL, null, colorTheme),
                    quote: quotes[pos - 1]
                });
            }
        }
    } catch (error) {
        Logger.info(`[${error.name}][Lifestyle] failed to fetch data for lifestyle quote`, {error});
    }
};

export {getLifestyleFront, getLifestyleFrontFormat, getLifestyleChannelFormat, getLifestyleQuote};
